import React, { useEffect, useState } from 'react';
import { Slider } from '@wajeez-web/components/ui';
import { Skeleton } from 'antd';
import { Line } from 'rc-progress';
import { isMobile } from '@wajeez-web/components/helpers';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { MediaHelpers } from '@wajeez-web/core/api';
import { useNavigate } from 'react-router-dom';

import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';
import { SeeMoreModal } from '../modals';
import { TextTrimmer } from '../layouts/TextTrimmer';
import {
  useContentDispatch,
  useContentSelector,
  SessionActions,
} from '@wajeez-web/stores/content';

export interface HorizantalListParameters {
  title: string | null;
  seeAllTitle: string | null;
  withProgress: 'False' | 'True';
}

export function HorizantalList({
  component,
  context,
  isLastSibling,
  pageSize,
  pageDataId,
  pageDataContextId,
}: BaseComponentProps) {
  const navigate = useNavigate();
  const { continueListeningProgresses } = useContentSelector(
    (state) => state.sessions
  );
  const dispatch = useContentDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getParameterValue, contents, contentsRequestStatus } =
    useComponentData<HorizantalListParameters>(
      component,
      context,
      undefined,
      pageSize
    );

  const { isActive } = useSkeletonHelper(contentsRequestStatus);

  const seeAllTitle = getParameterValue('seeAllTitle');
  const title = getParameterValue('title');
  const withProgress = getParameterValue('withProgress');
  const isContentorLoading = contents?.length;

  useEffect(() => {
    if (withProgress === 'True' && contents?.length) {
      dispatch(
        SessionActions.getContinueListeningProgressesAsync({
          contentEntityIds: contents?.map((content) => content.id as string),
          uniqueId: component?.uniqueId as number,
        })
      );
    }
  }, [contents]);

  return isContentorLoading ? (
    <div className="w-full space-y-[17px] sm:space-y-[7px]">
      <div className="flex justify-between px-4 sm:px-0">
        <div className="font-bold text-[20px] sm:text-[18px] sm:leading-[30px] sm:text-[#27160E]">
          {title}
        </div>
        {seeAllTitle && (
          <>
            <div
              className="sm:hidden text-[17px]"
              onClick={() => setIsModalOpen(true)}
            >
              {seeAllTitle}
            </div>
            <div
              className="hidden sm:block h-[33px] -translate-y-[6px] pt-[7px] pb-[8px] px-[18px] bg-[#F2F3F5] rounded-[21px] text-[15px] leading-[18px] font-medium text-black cursor-pointer"
              onClick={() => {
                if (!pageDataContextId) {
                  navigate(
                    `/content/see-more?pageDataId=${pageDataId}&componentId=${component?.id}`
                  );
                } else {
                  navigate(
                    `/content/categories/see-more?pageDataId=${pageDataId}&pageDataContextId=${pageDataContextId}&componentId=${component?.id}&contextId=${context?.id}`
                  );
                }
              }}
            >
              {seeAllTitle}
            </div>
          </>
        )}
      </div>
      <div>
        <div
          className={`w-full sm:pb-[28px] ${
            !isLastSibling && 'border-b-[0.5px]'
          } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider`}
        >
          <Slider customStyle="pb-10 px-2 sm:pb-0 sm:px-0 sm:mr-[-30px] desktop:mr-[-20px]">
            {contents?.slice(0, pageSize).map((content, i) => (
              <HorizantalListItem
                key={content.id}
                component={{ ...component?.itemTemplate, uniqueId: i }}
                context={content}
                progress={
                  continueListeningProgresses.data[
                    component?.uniqueId as number
                  ]?.find((progress) => progress.contentEntityId === content.id)
                    ?.currentProgress
                }
              />
            ))}
          </Slider>
        </div>
      </div>
      {isModalOpen && (
        <SeeMoreModal
          title={title}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          component={component}
          context={context}
          type={component?.type}
        />
      )}
    </div>
  ) : contentsRequestStatus === 'loading' ||
    contentsRequestStatus === 'failed' ? (
    <HorizantalListSkeleton isLastSibling={isLastSibling} isActive={isActive} />
  ) : null;
}

export const HorizantalListSkeleton = ({
  isLastSibling,
  isActive = true,
}: {
  isLastSibling: boolean | 0 | undefined;
  isActive?: boolean;
}) => {
  return (
    <div className="w-full pr-2">
      <div className="pr-2 sm:pr-0">
        <Skeleton.Button
          active={isActive}
          shape="square"
          className="!w-[200px] !h-[30px] dark:bg-dark-medium"
        />
      </div>
      <div
        className={`mt-3 w-full ${
          !isLastSibling && 'border-b-[0.5px]'
        } border-[#C6C6C8] dark:border-dark-divider sm:border-none`}
      >
        <Slider customStyle="pb-10 px-2 sm:pb-0 sm:px-0 sm:mr-[-30px]">
          {Array.from({ length: 10 }).map((_, i) => {
            return (
              <div
                key={i}
                className="'w-[134px] sm:w-full sm:px-[30px] !flex flex-col justify-center items-start sm:items-end"
              >
                <Skeleton.Node
                  active={isActive}
                  className="!h-[134px] !w-full sm:!h-[150px] 2xl:!h-[130px] dark:bg-dark-medium !rounded-[10px]"
                >
                  <div />
                </Skeleton.Node>
                <Skeleton.Button
                  active={isActive}
                  className="!w-[73px] !h-[16px] mt-2 sm:mt-[31px] dark:bg-dark-medium"
                />
                <Skeleton.Button
                  active={isActive}
                  className="!w-[103px] !h-[16px] mt-2 dark:bg-dark-medium"
                />
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export interface HorizantalListItemParameters {
  image: string | null;
}

export const HorizantalListItem = React.forwardRef<
  HTMLDivElement,
  BaseComponentProps
>(({ component, context, widthStyle, progress }, ref) => {
  const { getParameterValue, content, isLockedContent } =
    useComponentData<HorizantalListItemParameters>(component, context);

  const image = getParameterValue('image');
  return (
    <div
      ref={ref}
      className={`flex-col ${
        widthStyle || 'w-[134px] sm:w-full sm:px-[30px] desktop:px-[20px]'
      } items-start justify-start`}
    >
      {' '}
      <ClickableComponentLayout content={content}>
        <div
          style={{
            backgroundImage:
              'url(' + MediaHelpers.getPublicMediaById(image) + ')',
          }}
          className={`relative h-[134px] w-full sm:h-[auto] ${
            !widthStyle && 'desktop:h-[130px]'
          } border dark:border-none rounded-[10px] mb-[4px] sm:mb-0 overflow-hidden`}
        >
          <img
            src={MediaHelpers.getPublicMediaById(image)}
            alt="content"
            className="w-full h-full"
          />
          {isLockedContent && content?.contentEntityType !== 'FICTION' && (
            <div className=" w-[31px] h-[31px] absolute bottom-[10px] left-[10px]">
              <img
                src="../../assets/images/lock.png"
                alt="lock"
                className="block w-full h-full"
              />
            </div>
          )}
          <div className=" absolute top-[3px] left-0 w-full h-full rounded-[12px] overflow-hidden">
            {progress ? (
              <div className="absolute bottom-2 px-2 sm:px-0 sm:bottom-0 left-0 w-full justify-start items-center">
                <Line
                  percent={progress * 100}
                  strokeWidth={isMobile() ? 3 : 2}
                  trailWidth={isMobile() ? 3 : 2}
                  strokeColor={isMobile() ? '#fff' : '#38A7B4'}
                  trailColor={isMobile() ? '#a9acb2' : '#E9EDF7'}
                  style={{
                    transform: 'rotate(180deg)',
                    minWidth: '100%',
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="mt-1 sm:mt-[13px] text-[15px] sm:font-bold sm:text-[#545454] sm:text-right">
          <TextTrimmer textInput={content?.title} linesNumber={2} />
        </div>
        <div className="mt-1 sm:mt-0 text-[12px] sm:text-[15px] sm:font-medium text-[#3C3C4399] dark:text-dark-light sm:text-[#545454] sm:text-right">
          <TextTrimmer
            textInput={content?.authors?.toString().replaceAll(',', ' و ')}
            linesNumber={2}
          />
        </div>
      </ClickableComponentLayout>
    </div>
  );
});
